/**
 * Модалка выбора товара — единый компонент на весь проект.
 *
 * Используется из:
 *   - user/projectspec (ProductSelectionModal.open({context:'specification', ...}))
 *   - manager-request (ProductSelectionModal.open({context:'manager_request', ...}))
 *   - любого другого места через событие 'product-modal:open'
 *
 * Публичный API (совместимость со старыми вызовами):
 *   window.ProductSelectionModal.open(options)
 *   window.ProductSelectionModal.close()
 *   window.ProductSelectionModal.getCallback()
 *
 * Alpine-компонент productSelectionModal держит state и рендерит <dialog> с x-show-блоками:
 *   - list: поиск + фильтры + карточки товаров
 *   - detail: страница товара с выбором размера и кнопкой "Add to project"
 *
 * AJAX вызовы через apiRequest. Рендер HTML (карточки товаров, детали) пока приходит с сервера
 * как HTML (innerHTML), т.к. эти шаблоны используются также на обычных страницах и мигрируются
 * отдельно после включения Laravel Resources.
 *
 * Plugin-зависимости (jQuery остаётся до миграции Slick→Swiper):
 *   - customSelect — jQuery-плагин на размеры товара
 *   - Slick slider / Fancybox — инициализируются через window.initProductSliders/Gallery
 */

import Alpine from 'alpinejs';
import { apiRequest } from '../../api.js';
import { openDialog, closeDialog } from '../../dialog-utils.js';

// DOM helpers — делают код читаемым при работе с this.$root
const q = (root, sel) => root.querySelector(sel);
const qa = (root, sel) => root.querySelectorAll(sel);
const show = el => { if (el) el.style.display = ''; };
const hide = el => { if (el) el.style.display = 'none'; };
const setHtml = (el, html) => { if (el) el.innerHTML = html; };

Alpine.data('productSelectionModal', () => ({
    // State
    isOpen: false,
    view: 'list', // 'list' | 'detail'
    callback: null,
    context: '',
    contextData: {},
    selectedProduct: null,
    currentPage: 1,
    totalPages: 1,
    isLoading: false,
    currentFilters: {},
    searchTerm: '',
    _searchTimeout: null,
    _filtersManager: null,
    _loadingManager: null,

    init() {
        document.addEventListener('product-modal:open', e => this.open(e.detail || {}));
        document.addEventListener('product-modal:close', () => this.close());

        window.ProductSelectionModal = {
            open: options => this.open(options || {}),
            close: () => this.close(),
            getCallback: () => this.callback,
            reset: () => this.reset(),
            loadProducts: (filters, append) => this.loadProducts(filters, append),
            selectProduct: id => this.selectProduct(id),
        };

        // Cleanup state ТОЛЬКО после полного закрытия (event 'close' на native <dialog>
        // диспатчится в момент close() — после нашей анимации). Reset чистит innerHTML,
        // если делать это до анимации — модалка визуально схлопывается до пустой.
        this.$refs.dialog?.addEventListener('close', () => {
            this.reset();
            this.isOpen = false;
        });
    },

    open(options) {
        this.callback = options.onSelect || null;
        this.context = options.context || '';
        this.contextData = options.data || {};

        this.reset();
        openDialog(this.$refs.dialog);
        this.isOpen = true;

        this.loadCategories();
    },

    close() {
        // Запускаем анимацию. Reset state — в обработчике 'close' event (см. init()).
        closeDialog(this.$refs.dialog);
    },

    reset() {
        this.view = 'list';
        this.selectedProduct = null;
        this.currentPage = 1;
        this.totalPages = 1;
        this.isLoading = false;
        this.currentFilters = {};
        this.searchTerm = '';
        clearTimeout(this._searchTimeout);

        const root = this.$root;
        setHtml(q(root, '.products-container'), '');
        setHtml(q(root, '#modal_product_list_filter'), '');

        const detail = q(root, '.product-detail-section');
        if (detail) {
            detail.innerHTML = '';
            hide(detail);
        }
        show(q(root, '#modal-products-list'));
        show(q(root, '#modal_product_list_filter'));
        show(q(root, '.page-header'));
        hide(q(root, '.product_list_catalog_pagination'));
        hide(q(root, '#modal-back-link'));
    },

    showDetail() {
        const root = this.$root;
        this.view = 'detail';
        hide(q(root, '#modal-products-list'));
        hide(q(root, '#modal_product_list_filter'));
        hide(q(root, '.page-header'));
        show(q(root, '.product-detail-section'));
        show(q(root, '#modal-back-link'));
    },

    showList() {
        const root = this.$root;
        this.view = 'list';
        hide(q(root, '.product-detail-section'));
        show(q(root, '#modal-products-list'));
        show(q(root, '#modal_product_list_filter'));
        show(q(root, '.page-header'));
        hide(q(root, '#modal-back-link'));
        this.selectedProduct = null;
    },

    async loadCategories() {
        try {
            const response = await apiRequest('/ajax/filters', { method: 'GET' });
            if (!response?.success || !response.filtersHtml) return;

            setHtml(q(this.$root, '#modal_product_list_filter'), response.filtersHtml);

            if (typeof window.ProductLoadingManager !== 'undefined') {
                this._loadingManager = new window.ProductLoadingManager({
                    containerSelector: '#modal-products-list',
                    productsSelector: '.products-container',
                });
            }

            if (typeof window.ProductFiltersManager !== 'undefined') {
                this._filtersManager = new window.ProductFiltersManager({
                    context: 'modal',
                    filterId: 'modal_product_list_filter',
                    onFilterChange: () => this.applyFilters(),
                });
            }

            this.loadProducts({});
        } catch (e) {
            console.error('Error loading filters:', e);
        }
    },

    async loadProducts(filters = {}, append = false) {
        if (this.isLoading) return;
        this.isLoading = true;

        const root = this.$root;
        const productsContainer = q(root, '.products-container');

        if (!append) {
            this.currentPage = 1;
            this.currentFilters = filters;
        }

        if (!append && this._loadingManager) {
            this._loadingManager.showLoading();
        } else if (append) {
            const loadBtn = q(root, '#modal-load-more-button');
            if (loadBtn) {
                loadBtn.disabled = true;
                loadBtn.innerHTML = '<span class="spinner-border spinner-border-sm"></span> Loading...';
            }
        }

        const data = new FormData();
        data.append('cat_id', filters.categoryId || 0);
        if (filters.search) data.append('search[]', filters.search);
        data.append('modal', 1);
        data.append('is_wishlist', 0);
        data.append('page', this.currentPage);

        if (filters.categories) filters.categories.forEach(v => data.append('category[]', v));
        if (filters.subcategories) filters.subcategories.forEach(v => data.append('subcategory[]', v));
        if (filters.materials) filters.materials.forEach(v => data.append('material[]', v));
        if (filters.brands) filters.brands.forEach(v => data.append('brand[]', v));

        try {
            const response = await apiRequest('/project/get/products', {
                method: 'POST',
                body: data,
            });

            if (response && response.html_products) {
                if (this._loadingManager) {
                    this._loadingManager.updateProducts(response.html_products, append);
                } else if (productsContainer) {
                    if (append) {
                        productsContainer.insertAdjacentHTML('beforeend', response.html_products);
                    } else {
                        productsContainer.innerHTML = response.html_products;
                    }
                }

                if (response.qty_pages) this.totalPages = response.qty_pages;

                const pagination = q(root, '.product_list_catalog_pagination');
                if (this.currentPage < this.totalPages) show(pagination);
                else hide(pagination);

                this.bindProductClicks();
            } else if (!append) {
                const msg = window.__emptyProductsMessage || 'No products found';
                const html = `<p class="text-center text-muted py-5">${msg}</p>`;
                if (this._loadingManager) {
                    this._loadingManager.updateProducts(html);
                } else if (productsContainer) {
                    productsContainer.innerHTML = html;
                }
                hide(q(root, '.product_list_catalog_pagination'));
            }
        } catch (e) {
            console.error('Error loading products:', e);
            const msg = window.__loadingProductsError || 'Error loading products';
            if (!append) {
                const html = `<p class="text-center text-danger">${msg}</p>`;
                if (this._loadingManager) {
                    this._loadingManager.updateProducts(html);
                } else if (productsContainer) {
                    productsContainer.innerHTML = html;
                }
            } else if (this._loadingManager) {
                this._loadingManager.hideLoading();
            }
        } finally {
            const loadMoreLabel = window.__showMoreLabel || 'Show More';
            const loadBtn = q(root, '#modal-load-more-button');
            if (loadBtn) {
                loadBtn.disabled = false;
                loadBtn.innerHTML = loadMoreLabel;
            }
            this.isLoading = false;
        }
    },

    bindProductClicks() {
        // Убираем прошлые listener-ы через клонирование — проще, чем трекать каждый
        qa(this.$root, '.modal-product-item').forEach(item => {
            const clone = item.cloneNode(true);
            item.parentNode.replaceChild(clone, item);
            clone.addEventListener('click', () => {
                const productId = clone.dataset.productId;
                if (productId) this.selectProduct(productId);
            });
        });
    },

    async selectProduct(productId) {
        const root = this.$root;
        const detail = q(root, '.product-detail-section');

        setHtml(detail,
            '<div class="text-center py-5"><div class="spinner-border" role="status"></div></div>'
        );

        try {
            const data = new FormData();
            data.append('modal_view', 1);

            const response = await apiRequest(`/project/get/product/${productId}`, {
                method: 'POST',
                body: data,
            });

            if (response && response.html) {
                setHtml(detail, response.html);
                this.selectedProduct = response.product;
                setTimeout(() => this._initProductDetailWidgets(response.product), 100);
                this.showDetail();
            }
        } catch (e) {
            console.error('Error loading product details:', e);
            const msg = window.__loadingProductError || 'Error loading product';
            setHtml(detail, `<p class="text-center text-danger py-5">${msg}</p>`);
            window.showToast?.('error', msg);
        }
    },

    _initProductDetailWidgets(product) {
        const root = this.$root;
        const detail = q(root, '.product-detail-section');
        if (!detail) return;

        // customSelect остаётся на jQuery — плагин не мигрирован
        if (typeof $ !== 'undefined' && $.fn.customSelect) {
            const sel1 = $(detail).find('#product_select_dim');
            const sel2 = $(detail).find('#product_select_dim_mm');
            if (sel1.length) sel1.customSelect();
            if (sel2.length) sel2.customSelect();
        }

        // Обновление цены по выбору размера
        const priceSelect = q(detail, 'select.js_select-price');
        if (priceSelect) {
            priceSelect.addEventListener('change', () => {
                const opt = priceSelect.options[priceSelect.selectedIndex];
                const price = Number(opt?.dataset.price || 0);
                const priceLabel = q(detail, '.js_product-price');
                const priceValue = document.getElementById('js_product_price_modal');
                if (price > 0) {
                    if (priceValue) priceValue.textContent = price;
                    show(priceLabel);
                } else {
                    hide(priceLabel);
                }
            });
        }

        if (typeof window.initProductSliders === 'function') {
            window.initProductSliders(detail);
        }
        if (typeof window.initProductGallery === 'function') {
            window.initProductGallery(detail);
        }
        if (typeof window.initAddToProjectButton === 'function') {
            window.initAddToProjectButton(
                detail,
                product.id,
                window.userProjects,
                null,
                this.contextData,
            );
        }
    },

    applyFilters() {
        const filters = this._filtersManager ? this._filtersManager.getSelectedFilters() : {
            categories: [], subcategories: [], materials: [], brands: [], tags: [],
        };
        filters.search = this.searchTerm;
        this.loadProducts(filters);
    },

    onSearchInput() {
        clearTimeout(this._searchTimeout);
        this._searchTimeout = setTimeout(() => this.performSearch(), 500);
    },

    onSearchSubmit(e) {
        if (e) e.preventDefault();
        clearTimeout(this._searchTimeout);
        this.performSearch();
    },

    performSearch() {
        const filters = {
            categories: [], subcategories: [], materials: [], brands: [],
            search: this.searchTerm,
        };

        qa(this.$root, '#modal_product_list_filter input[type="checkbox"]:checked').forEach(cb => {
            const type = cb.dataset.type;
            const id = parseInt(cb.dataset.id, 10);
            if (type === 'category') filters.categories.push(id);
            else if (type === 'subcategory') filters.subcategories.push(id);
            else if (type === 'material') filters.materials.push(id);
            else if (type === 'brand') filters.brands.push(id);
        });

        this.loadProducts(filters);
    },

    loadMore() {
        if (this.isLoading || this.currentPage >= this.totalPages) return;
        this.currentPage++;
        this.loadProducts(this.currentFilters, true);
    },
}));
